import { ButtonGroup, styled } from '@mui/material';
import { forwardRef } from 'react';
import Badge from 'shell/components/Badge';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';
import Tooltip from 'shell/components/Tooltip';

const CustomButtonGroup = styled(ButtonGroup, {
    shouldForwardProp: (prop) => prop !== 'numOptions' && prop !== 'maxWidth',
})(({ theme, maxWidth, numOptions }) => {
    const gap = theme.spacing(1);
    return {
        gap,
        borderRadius: '40px',
        padding: theme.spacing(1),
        background: theme.palette.ensightful.neutral[200],
        '& > *': {
            width: `calc((100% - ${numOptions - 1} * ${gap}) / ${numOptions})`,
            minWidth: 0,
            display: 'flex',
        },
        '& .MuiButton-root': {
            flex: 1,
        },
        '& .MuiBox-root': {
            flex: 1,
            width: '100%',
        },
        ...(maxWidth && {
            maxWidth,
            width: '100%',
        }),
    };
});

const CustomBadge = styled(Badge)(({ theme }) => {
    return {
        '& .MuiBadge-dot': {
            borderRadius: '50%',
            width: '12px',
            height: '12px',
            background: theme.palette.ensightful.error[600],
            top: '4px',
            right: '4px',
        },
    };
});

const BadgeButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'hasMaxWidth',
})(({ theme, isSelected, hasMaxWidth }) => ({
    '&&': {
        width: '100%',

        borderRadius: '40px',
        ...(hasMaxWidth && {
            paddingInline: theme.spacing(2),
        }),
    },
    ...(hasMaxWidth && {
        fontSize: 'clamp(0.6rem, 2vw, 0.875rem)',
        '& .MuiButton-startIcon': {
            marginRight: theme.spacing(0.5),
        },
    }),
    '&:focus-visible': {
        outlineColor: isSelected ? theme.palette.ensightful.secondary[100] : theme.palette.ensightful.secondary[400],
        outlineWidth: '3px',
        outlineOffset: '-5px',
    },
    '& > div': {
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
    },
}));

const CustomButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'hasMaxWidth',
})(({ theme, isSelected, hasMaxWidth }) => ({
    '&&': {
        borderRadius: '40px',
        ...(hasMaxWidth && {
            paddingInline: theme.spacing(2),
        }),
    },
    ...(hasMaxWidth && {
        fontSize: 'clamp(0.6rem, 2vw, 0.875rem)',
        '& .MuiButton-startIcon': {
            marginRight: theme.spacing(0.5),
        },
    }),
    '&:focus-visible': {
        outlineColor: isSelected ? theme.palette.ensightful.secondary[100] : theme.palette.ensightful.secondary[400],
        outlineWidth: '3px',
        outlineOffset: '-5px',
    },
    '& > div': {
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
    },
}));

export default forwardRef(({ maxWidth, options, selected, onChange, ...rest }, ref) => {
    if (!options.length) throw new Error(`Shell: options cannot be empty in component ButtonGroup.`);
    if (!selected) throw new Error(`Shell: selected cannot be null in component ButtonGroup.`);

    const hasMaxWidth = Boolean(maxWidth);

    return (
        <CustomButtonGroup ref={ref} {...rest} disableElevation maxWidth={maxWidth} numOptions={options.length}>
            {options.map((option) => {
                const isSelected = selected === option.key;
                const startIcon = option.icon ? <Icon kind={option.icon} /> : undefined;
                const ButtonComponent = option.badge ? BadgeButton : CustomButton;

                const button = (
                    <ButtonComponent
                        color={isSelected ? 'primary' : 'neutral'}
                        disabled={option.disabled}
                        hasMaxWidth={hasMaxWidth}
                        isSelected={isSelected}
                        startIcon={startIcon}
                        variant={isSelected ? 'primary' : 'tertiary'}
                        onClick={() => onChange(option.key)}
                        tooltip={option.tooltip && { component: option.tooltip }}
                    >
                        {option.value}
                    </ButtonComponent>
                );

                const wrappedButton = option.badge ? (
                    <CustomBadge key={option.key} variant={'dot'}>
                        {button}
                    </CustomBadge>
                ) : (
                    button
                );

                return (
                    <Tooltip key={option.key} content={option.value} disableHoverListener={!hasMaxWidth}>
                        {wrappedButton}
                    </Tooltip>
                );
            })}
        </CustomButtonGroup>
    );
});
